/**
 * GTM 마케팅 전환 신호 유틸 — 분석 이벤트 표준의 "명시적 예외" 경계
 *
 * 이 모듈은 제품 분석 이벤트 체계(`@zep/logger/tracking`, docs/event-tracking)와
 * 의도적으로 분리된 별도 경로다. 여기의 `purchase`는 제품 분석용 track 이벤트가
 * 아니라, GTM 컨테이너의 Custom Event 트리거가 소비하는 광고 전환(Google Ads,
 * Meta 등) 전용 dataLayer 신호이기 때문이다.
 *
 * - 스키마는 GTM/광고 플랫폼 표준 계약(event/value/currency/transaction_id)을
 *   따르며, 제품 EventMap에 등록하지 않는다 (채널 중립 신호를 제품 분석
 *   파이프라인과 결합하지 않기 위함).
 * - window.dataLayer 직접 접근은 이 모듈 안에만 허용한다. 결제 hook/컴포넌트는
 *   반드시 이 유틸을 통해서만 push한다 (quiz-app GtmMarketingEventLogger와
 *   동일한 경계 설계).
 * - 제품 분석 이벤트를 추가할 때는 이 모듈이 아니라 기존 track 표준을 따를 것.
 *
 * 결제 전환 추적의 브라우저 측 provider는 GTM 하나로 고정한다. 결제 코드가
 * 이 GTM 명칭 유틸을 직접 import하는 것은 의도된 결정이며, provider-neutral
 * facade를 두지 않는 근거는 ADR 참조:
 * https://github.com/zep-us/ADR/blob/main/decisions/web-app/payment-conversion-gtm-only.md
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

type GtmPurchaseEventParams = {
  value: number;
  currency: string;
  transactionId: string;
};

const PUSHED_STORAGE_KEY_PREFIX = 'gtm_purchase_pushed:';
const PENDING_AMOUNT_KEY_PREFIX = 'gtm_purchase_amount:';

type GtmPurchaseAmount = {
  value: number;
  currency: string;
};

/**
 * PG 리다이렉트 전에 실제 청구액을 보관한다.
 * (일시납처럼 결제가 풀 리다이렉트를 거치면 인메모리 값이 유실되므로,
 * 완료 페이지가 takeGtmPurchaseAmount로 꺼내 purchase 신호에 사용한다)
 */
export const stashGtmPurchaseAmount = (
  transactionId: string,
  amount: GtmPurchaseAmount,
) => {
  if (typeof window === 'undefined' || !transactionId) return;
  try {
    window.sessionStorage.setItem(
      `${PENDING_AMOUNT_KEY_PREFIX}${transactionId}`,
      JSON.stringify(amount),
    );
  } catch {
    // 보관 실패 시 완료 페이지의 fallback 값(salePrice)이 사용된다.
  }
};

export const takeGtmPurchaseAmount = (
  transactionId: string,
): GtmPurchaseAmount | null => {
  if (typeof window === 'undefined' || !transactionId) return null;
  try {
    const raw = window.sessionStorage.getItem(
      `${PENDING_AMOUNT_KEY_PREFIX}${transactionId}`,
    );
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof (parsed as GtmPurchaseAmount)?.value !== 'number' ||
      typeof (parsed as GtmPurchaseAmount)?.currency !== 'string'
    ) {
      return null;
    }
    return parsed as GtmPurchaseAmount;
  } catch {
    return null;
  }
};

let hasGrantedGtmConsent = false;

/**
 * 동의(grant) 전에 발생한 purchase 이벤트 보류 큐.
 *
 * purchase payload(value/currency/transaction_id)는 동의 확인 전에는
 * dataLayer에 넣지 않는다. 다만 React 이펙트 순서상 완료 페이지(자식)의
 * push가 AcceptCookieWrapper(부모)의 동의 확인보다 먼저 실행될 수 있어,
 * 즉시 버리는 대신 여기 보류했다가 grant 시점에 flush한다.
 * 동의가 끝내 grant되지 않으면(EU 미수락 등) 어디에도 push되지 않는다.
 */
const pendingPurchaseEvents: GtmPurchaseEventParams[] = [];

const pushPurchaseToDataLayer = ({
  value,
  currency,
  transactionId,
}: GtmPurchaseEventParams) => {
  const storageKey = `${PUSHED_STORAGE_KEY_PREFIX}${transactionId}`;
  try {
    if (window.sessionStorage.getItem(storageKey)) return;
    window.sessionStorage.setItem(storageKey, '1');
  } catch {
    // sessionStorage 접근 불가(시크릿 모드 등) 시 dedupe 없이 진행.
    // Google Ads는 transaction_id로 서버에서 한 번 더 dedupe한다.
  }

  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({
    event: 'purchase',
    value,
    currency,
    transaction_id: transactionId,
  });
};

/**
 * GTM Consent Mode 동의 상태를 granted로 업데이트한다.
 *
 * _document.tsx의 GTM 스니펫이 기본값을 denied로 초기화하므로,
 * 기존 쿠키 동의 흐름(AcceptCookieWrapper.onAcceptedCookie: EU는 배너 수락 시,
 * 비EU는 mount 시 자동 호출)에서 이 함수를 호출해 동의 이후에만
 * 광고/분석 storage 동작을 허용한다. grant 시 동의 전 보류된 purchase
 * 이벤트도 함께 flush한다.
 */
export const grantGtmMarketingConsent = () => {
  if (typeof window === 'undefined' || hasGrantedGtmConsent) return;
  hasGrantedGtmConsent = true;

  window.dataLayer = window.dataLayer ?? [];
  // GTM consent API는 배열이 아니라 arguments 객체 push를 요구한다.
  function gtag() {
    window.dataLayer?.push(arguments);
  }
  // @ts-expect-error -- gtag consent 커맨드는 arguments 기반 시그니처를 사용한다.
  gtag('consent', 'update', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  });

  while (pendingPurchaseEvents.length > 0) {
    const pending = pendingPurchaseEvents.shift();
    if (pending) pushPurchaseToDataLayer(pending);
  }
};

/**
 * 결제 완료 시 GTM dataLayer에 마케팅 전환용 `purchase` 이벤트를 push한다.
 *
 * 광고 채널(Google Ads, Meta 등) 전환 태그는 GTM 컨테이너에서 이 이벤트를
 * Custom Event 트리거로 받아 연결한다. 코드에는 채널별 태그를 심지 않는다.
 *
 * 동의(grantGtmMarketingConsent) 전에는 payload를 dataLayer에 넣지 않고
 * 보류 큐에만 담는다 (grant 시 flush, 미동의 시 폐기).
 * 새로고침/재방문으로 같은 주문이 중복 집계되지 않도록
 * transactionId 기준으로 세션당 1회만 push한다.
 */
export const pushGtmPurchaseEvent = (params: GtmPurchaseEventParams) => {
  if (typeof window === 'undefined') return;
  const { value, transactionId } = params;
  if (!transactionId || !Number.isFinite(value) || value <= 0) return;

  if (!hasGrantedGtmConsent) {
    pendingPurchaseEvents.push(params);
    return;
  }

  pushPurchaseToDataLayer(params);
};
